import { useAppSelector } from "@/redux/hooks";
import { StoreBrandingType } from "@/types/misc";
import useStoreType from "@/utils/hooks/useStoreType";
import { BrandclubStoreConfigUtils, hexadecimal } from "@brandclub/common-ui";
import { Skeleton, useTheme } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { StripePaymentForm } from "./StripePaymentForm";
import { CheckoutIntent } from "../../../types/CheckoutIntent";

const stripePromise = loadStripe(
  BrandclubStoreConfigUtils.getStripePublishableKey()
);

const Loading = () => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div>
        <Skeleton
          variant="rectangular"
          height={50}
          sx={{ backgroundColor: "#F6F8FA" }}
        />
      </div>
      <div>
        <Skeleton
          variant="rectangular"
          height={50}
          sx={{ backgroundColor: "#F6F8FA" }}
        />
      </div>
    </div>
  );
};

export const CheckoutPaymentAddForm = ({
  onCancel,
  onAddNewPaymentMethod,
}: {
  onCancel: () => void;
  onAddNewPaymentMethod: (paymentMethodType: string) => void;
}) => {
  const storeType = useStoreType();
  const theme = useTheme();
  const cartProducts = useAppSelector((state) => state.checkout.products);
  const [state] = useCheckoutUI();
  // @ts-ignore
  const checkoutIntent = state.context.checkoutIntent as CheckoutIntent;
  const clientSecret = checkoutIntent?.stripePaymentIntent?.client_secret;
  const checkoutIntentId = checkoutIntent?.checkoutIntentId;
  if (!clientSecret || !checkoutIntentId) {
    return <Loading />;
  }
  // this is used to display the order summary in the apple pay or google pay form
  const lineItems =
    checkoutIntent?.items?.map((item) => ({
      name: cartProducts?.[item.stacklineSku]?.title ?? "",
      amount: Math.round(item.retailPrice * 100),
    })) ?? [];
  if ((checkoutIntent?.estimatedOrderSummary?.shippingFees ?? 0) > 0) {
    lineItems.push({
      name: "Shipping fees",
      amount: Math.round(
        (checkoutIntent?.estimatedOrderSummary?.shippingFees ?? 0) * 100
      ),
    });
  }
  if ((checkoutIntent?.estimatedOrderSummary?.shippingDiscount ?? 0) > 0) {
    lineItems.push({
      name: "Shipping Discount",
      amount: Math.round(
        (checkoutIntent?.estimatedOrderSummary?.shippingDiscount ?? 0) * 100
      ),
    });
  }
  if ((checkoutIntent?.estimatedOrderSummary?.giftCard ?? 0) > 0) {
    lineItems.push({
      name: "Gift Card",
      amount: Math.round(
        (checkoutIntent?.estimatedOrderSummary?.giftCard ?? 0) * 100
      ),
    });
  }
  if ((checkoutIntent?.estimatedOrderSummary?.tax ?? 0) > 0) {
    lineItems.push({
      name: "Tax",
      amount: Math.round(
        (checkoutIntent?.estimatedOrderSummary?.tax ?? 0) * 100
      ),
    });
  }

  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const fontFamily =
    isDtc && theme.dtcFonts?.fontFamily
      ? theme.dtcFonts?.fontFamily
      : "Poppins";
  const fontFamilyLink =
    isDtc && theme.dtcFonts?.fontFamilyLink
      ? theme.dtcFonts?.fontFamilyLink
      : "https://fonts.googleapis.com/css2?family=Poppins";

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        paymentMethodCreation: "manual",
        fonts: [
          {
            cssSrc: fontFamilyLink,
          },
        ],
        appearance: {
          labels: "floating",
          theme: "stripe",
          variables: {
            fontFamily: fontFamily,
            colorText: theme.palette.primary.main,
            colorTextPlaceholder: hexadecimal(theme.palette.primary.main)(50),
            fontSizeSm: "12px",
            borderRadius: "99px",
          },
          rules: {
            ".Input": {
              borderRadius: "8px",
              shadows: "none",
              fontSize: "14px",
              border: `1px solid #dcdddc`,
            },
            ".Label": {
              color: hexadecimal(theme.palette.primary.main)(30),
              fontWeight: "semibold",
            },
            ".Label--resting": {
              fontSize: "16px",
            },
            ".Label--floating": {
              fontSize: "12px",
            },
          },
        },
      }}
    >
      <StripePaymentForm
        onCancel={onCancel}
        onShippingAddressChange={async (event) => {
          // console.log("onShippingAddressChange", event);
          event.resolve();
        }}
        onShippingRateChange={async (event) => {
          // console.log("onShippingRateChange", event);
          event.resolve();
        }}
        lineItems={lineItems}
        shippingRates={
          checkoutIntent?.shippingOptions?.map((option) => ({
            id: option.id,
            amount: Math.round(option.amount * 100),
            displayName: option.name,
            deliveryEstimate: option.deliveryEstimate,
          })) ?? [
            {
              id: "standard",
              amount: Math.round(
                (checkoutIntent?.estimatedOrderSummary?.shippingFees ?? 0) * 100
              ),
              displayName: "2-5 business days",
              deliveryEstimate: {
                minimum: {
                  unit: "business_day",
                  value: 2,
                },
                maximum: {
                  unit: "business_day",
                  value: 5,
                },
              },
            },
          ]
        }
        onSuccess={async (paymentMethodType, _paymentMethod) => {
          onAddNewPaymentMethod(paymentMethodType);
        }}
        onError={(error) => {
          console.error(error);
        }}
      />
    </Elements>
  );
};
